import React, { useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogTitle, Slide, Stack, Typography, Avatar, IconButton, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { PhoneDisconnect, VideoCamera, Phone } from 'phosphor-react';
import createAvatar from '../utils/createAvatar';
import { useSelector, useDispatch } from 'react-redux';
import { denyCall, acceptCall } from '../redux/slices/call';
import useSocket from '../hooks/useSocket';
import { PROFILE_IMAGE_URL } from '../config'; // Import PROFILE_IMAGE_URL

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const CallingModal = () => { // Removed props, now driven by Redux
  const theme = useTheme();
  const dispatch = useDispatch();
  const { denyCall: emitDenyCall, acceptCall: emitAcceptCall, BASE_URL } = useSocket(); // Socket emit functions
  const { incomingCallModalOpen, activeCallDetails } = useSelector((state) => state.call);

  const callerName = activeCallDetails?.callerName || 'Unknown';
  const callerId = activeCallDetails?.callerId;
  const receiverId = activeCallDetails?.receiverId; // Our user ID
  const type = activeCallDetails?.type || 'audio';
  const roomId = activeCallDetails?.roomId;

  const avatarProps = createAvatar(callerName);
  const avatarSrc = callerId
    ? `${PROFILE_IMAGE_URL}${callerId}` // Use PROFILE_IMAGE_URL
    : undefined;

  const handleDenyCall = () => {
    if (callerId) {
      emitDenyCall(callerId); // Emit denyCall via socket
    }
    dispatch(denyCall()); // Update Redux state
  };

  const handleAcceptCall = () => {
    if (activeCallDetails) {
      emitAcceptCall(activeCallDetails); // Emit acceptCall via socket
    }
    // Redux state will be updated by the 'callAccepted' socket event listener
  };

  return (
    <Dialog
      open={incomingCallModalOpen}
      TransitionComponent={Transition}
      onClose={handleDenyCall} // Allow closing with backdrop click to deny
      aria-describedby="calling-modal-description"
      sx={{ zIndex: 1400, '& .MuiDialog-paper': { borderRadius: 4 } }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 0 }}>
        <Typography variant="h5" sx={{ color: theme.palette.text.primary }}>
          {type === 'audio' ? 'Incoming Audio Call' : 'Incoming Video Call'}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={3} alignItems="center" justifyContent="center">
          <Avatar
            src={avatarSrc}
            alt={callerName}
            sx={{
              width: 120,
              height: 120,
              fontSize: '3rem',
              bgcolor: avatarProps.color,
              color: theme.palette.getContrastText(avatarProps.color),
            }}
          >
            {!avatarSrc && avatarProps.name}
          </Avatar>
          <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
            {callerName}
          </Typography>
          <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary }}>
            {type === 'audio' ? 'Ringing...' : 'Video Ringing...'}
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <IconButton
              onClick={handleDenyCall}
              sx={{
                backgroundColor: theme.palette.error.main,
                color: theme.palette.common.white,
                width: 64,
                height: 64,
                '&:hover': {
                  backgroundColor: theme.palette.error.dark,
                },
              }}
            >
              <PhoneDisconnect size={32} />
            </IconButton>
            <IconButton
              onClick={handleAcceptCall}
              sx={{
                backgroundColor: theme.palette.success.main,
                color: theme.palette.common.white,
                width: 64,
                height: 64,
                '&:hover': {
                  backgroundColor: theme.palette.success.dark,
                },
              }}
            >
              {type === 'audio' ? <Phone size={32} /> : <VideoCamera size={32} />}
            </IconButton>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default CallingModal;